import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Google Analytics Measurement ID
const GA_MEASUREMENT_ID = 'G-9M5LK9RDNS';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Track last tracked page to prevent double tracking (React StrictMode)
let lastTrackedPage = null;
let lastTrackedTime = 0;

/**
 * Detect platform/device type
 */
const getDeviceType = () => {
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('android')) return 'android';
  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  if (ua.includes('mobile')) return 'mobile';
  return 'desktop';
};

/**
 * Check if running in mobile app WebView
 */
const isInAppWebView = () => {
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('capacitor') || ua.includes('wv') || ua.includes('webview');
};

/**
 * Get screen resolution category
 */
const getScreenInfo = () => {
  const width = window.screen.width;
  const height = window.screen.height;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  
  let category;
  if (viewportWidth < 576) {
    category = 'mobile';
  } else if (viewportWidth < 768) {
    category = 'tablet-sm';
  } else if (viewportWidth < 992) {
    category = 'tablet';
  } else if (viewportWidth < 1200) {
    category = 'desktop';
  } else if (viewportWidth < 1400) {
    category = 'desktop-lg';
  } else {
    category = 'desktop-xl';
  }
  
  return {
    screen_width: width,
    screen_height: height,
    viewport_width: viewportWidth,
    viewport_height: viewportHeight,
    resolution: `${width}x${height}`,
    viewport: `${viewportWidth}x${viewportHeight}`,
    screen_category: category
  };
};

/**
 * Get browser info
 */
const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  let browser = 'Other';
  let version = '';
  
  if (ua.includes('Chrome') && !ua.includes('Edg')) {
    browser = 'Chrome';
    version = ua.match(/Chrome\/(\d+)/)?.[1] || '';
  } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
    browser = 'Safari';
    version = ua.match(/Version\/(\d+)/)?.[1] || '';
  } else if (ua.includes('Firefox')) {
    browser = 'Firefox';
    version = ua.match(/Firefox\/(\d+)/)?.[1] || '';
  } else if (ua.includes('Edg')) {
    browser = 'Edge';
    version = ua.match(/Edg\/(\d+)/)?.[1] || '';
  } else if (ua.includes('Opera') || ua.includes('OPR')) {
    browser = 'Opera';
    version = ua.match(/(?:Opera|OPR)\/(\d+)/)?.[1] || '';
  }
  
  return { browser, browser_version: version };
};

/**
 * Get OS info
 */
const getOSInfo = () => {
  const ua = navigator.userAgent;
  let os = 'Other';
  
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac OS')) os = 'macOS';
  else if (ua.includes('Linux') && !ua.includes('Android')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';
  
  return { os };
};

/**
 * Initialize gtag if not already loaded
 */
const ensureGtagLoaded = () => {
  if (typeof window.gtag === 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID);
  }
};

/**
 * Generate a session ID for tracking
 */
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substring(2, 15);
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

/**
 * Get or create visitor ID (persistent across sessions)
 */
const getVisitorId = () => {
  let visitorId = localStorage.getItem('analytics_visitor_id');
  if (!visitorId) {
    visitorId = 'vis_' + Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    localStorage.setItem('analytics_visitor_id', visitorId);
  }
  return visitorId;
};

/**
 * Send analytics to backend
 */
const sendBackendAnalytics = async (eventData) => {
  try {
    await fetch(`${BACKEND_URL}/api/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(eventData),
      keepalive: true
    });
  } catch (e) {
    // Silent fail
  }
};

/**
 * Google Analytics tracking component for SPA
 */
const GoogleAnalytics = () => {
  const location = useLocation();
  const isTracking = useRef(false);

  useEffect(() => {
    // Skip tracking for admin users
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        if (user.is_admin) {
          return; // Don't track admin users
        }
      }
    } catch (e) {
      // Continue with tracking if user check fails
    }
    
    // Prevent double tracking (React StrictMode runs effects twice)
    const currentPage = location.pathname + location.search;
    const now = Date.now();
    
    // Skip if same page tracked within last 500ms
    if (currentPage === lastTrackedPage && (now - lastTrackedTime) < 500) {
      return;
    }
    
    // Prevent concurrent tracking
    if (isTracking.current) return;
    isTracking.current = true;
    
    lastTrackedPage = currentPage;
    lastTrackedTime = now;
    
    ensureGtagLoaded();
    
    const deviceType = getDeviceType();
    const isWebView = isInAppWebView();
    const screenInfo = getScreenInfo();
    const browserInfo = getBrowserInfo();
    const osInfo = getOSInfo();
    
    // Determine platform
    let platform;
    if (isWebView) {
      platform = deviceType === 'ios' ? 'ios_app' : 'android_app';
    } else if (deviceType === 'android') {
      platform = 'android_web';
    } else if (deviceType === 'ios') {
      platform = 'ios_web';
    } else {
      platform = 'web_desktop';
    }
    
    // Track via Google Analytics
    if (typeof window.gtag !== 'undefined') {
      window.gtag('config', GA_MEASUREMENT_ID, {
        page_path: location.pathname + location.search,
        page_title: document.title,
      });
      
      window.gtag('event', 'page_view', {
        page_location: window.location.href,
        page_path: location.pathname + location.search,
        page_title: document.title,
        device_type: deviceType,
        platform: platform
      });
    }
    
    // Always send to backend for complete tracking
    sendBackendAnalytics({
      event_type: 'page_view',
      page_path: location.pathname,
      page_title: document.title,
      page_url: window.location.href,
      device_type: deviceType,
      platform: platform,
      is_webview: isWebView,
      ...screenInfo,
      ...browserInfo,
      ...osInfo,
      user_agent: navigator.userAgent,
      referrer: document.referrer,
      language: navigator.language,
      session_id: getSessionId(),
      visitor_id: getVisitorId(),
      timestamp: new Date().toISOString()
    }).finally(() => {
      isTracking.current = false;
    });
    
  }, [location]);

  return null;
};

/**
 * Track custom events
 */
export const trackEvent = (action, category, label = '', value = null) => {
  ensureGtagLoaded();
  
  const deviceType = getDeviceType();
  const isWebView = isInAppWebView();
  const screenInfo = getScreenInfo();
  
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
      device_type: deviceType
    });
  }
  
  sendBackendAnalytics({
    event_type: 'event',
    action,
    category,
    label,
    value,
    device_type: deviceType,
    platform: isWebView ? 'mobile_app' : (deviceType === 'desktop' ? 'web_desktop' : 'web_mobile'),
    ...screenInfo,
    session_id: getSessionId(),
    visitor_id: getVisitorId(),
    timestamp: new Date().toISOString()
  });
};

/**
 * Track user properties
 */
export const setUserProperties = (properties) => {
  ensureGtagLoaded();
  
  if (typeof window.gtag !== 'undefined') {
    window.gtag('set', 'user_properties', properties);
  }
};

/**
 * Track conversions
 */
export const trackConversion = (conversionType, data = {}) => {
  ensureGtagLoaded();
  
  const deviceType = getDeviceType();
  const isWebView = isInAppWebView();
  
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', conversionType, {
      ...data,
      send_to: GA_MEASUREMENT_ID
    });
  }
  
  sendBackendAnalytics({
    event_type: 'conversion',
    conversion_type: conversionType,
    ...data,
    device_type: deviceType,
    platform: isWebView ? 'mobile_app' : (deviceType === 'desktop' ? 'web_desktop' : 'web_mobile'),
    session_id: getSessionId(),
    visitor_id: getVisitorId(),
    timestamp: new Date().toISOString()
  });
};

export default GoogleAnalytics;
